import { createFileRoute } from '@tanstack/react-router'
import {
  Button,
  Card,
  Content as CardContent,
  Text as CardDescription,
  Footer as CardFooter,
  Header as CardHeader,
  Heading as CardTitle,
  LinkButton,
} from '@react-spectrum/s2'
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight'
import { Building, Check } from 'lucide-react'
import { useEffect } from 'react'
import { APP_ROUTES } from '@shared/app-routes'
import { trackAnalyticsEvent } from '@client/analytics'
import { getAuthSession } from '@rpc/auth-session.rpc'
import { getPublicPlans } from '@rpc/plans.rpc'
import Footer from '@/components/Footer'
import Header from '@/components/Header'
import { cn } from '@/lib/utils'
import {
  centeredButtonStyles,
  fullWidthCenteredButtonStyles,
  fullWidthStyles,
} from '@/styles/s2-macros'

const PLAN_RANK = {
  registeredUser: 0,
  premiumUser: 1,
  proUser: 2,
  enterpriseUser: 3,
} as const

type PlanId = keyof typeof PLAN_RANK

function resolveCurrentPlanId(plan?: string | null): PlanId {
  if (
    plan === 'registeredUser' ||
    plan === 'premiumUser' ||
    plan === 'proUser' ||
    plan === 'enterpriseUser'
  ) {
    return plan
  }
  return 'registeredUser'
}

function formatActionPlanName(planId: string, planName: string): string {
  if (planId === 'proUser') return 'Pro'
  if (planId === 'premiumUser') return 'Premium'
  return planName
}

function buildCheckoutRedirect(planId: string) {
  return `${APP_ROUTES.login}?redirect=${encodeURIComponent(
    `${APP_ROUTES.checkout}/${planId}`,
  )}`
}

export const Route = createFileRoute('/pricing')({
  loader: async () => {
    const [plans, session] = await Promise.all([
      getPublicPlans(),
      getAuthSession(),
    ])

    return { plans, session }
  },
  component: PricingPage,
})

function PricingPage() {
  const { plans, session } = Route.useLoaderData()
  const isLoggedIn = Boolean(session?.user)
  const currentPlanId = resolveCurrentPlanId(session?.user.billingPlan)
  const currentRank = PLAN_RANK[currentPlanId]

  // Separate plans
  const mainPlans = plans.filter(
    (p) => p.id !== 'enterpriseUser' && p.id !== 'freeUser',
  )
  const enterprisePlan = plans.find((p) => p.id === 'enterpriseUser')

  useEffect(() => {
    trackAnalyticsEvent('pricing_viewed', {
      current_plan_id: currentPlanId,
      is_authenticated: isLoggedIn,
      plan_count: mainPlans.length,
    })
  }, [currentPlanId, isLoggedIn, mainPlans.length])

  return (
    <div className="flex min-h-screen flex-col bg-background font-sans">
      <Header />

      <main
        id="main-content"
        className="mx-auto w-full max-w-7xl flex-1 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Simple, transparent pricing
          </h1>
        </div>

        {/* Main Plans Grid */}
        <div
          className={cn(
            'grid gap-8 mb-16',
            mainPlans.length === 2
              ? 'md:grid-cols-2 md:max-w-5xl md:mx-auto'
              : 'md:grid-cols-3',
          )}
        >
          {mainPlans.map((plan) => {
            const isBasic = plan.id === 'registeredUser'
            const isCurrentPlan = isLoggedIn && plan.id === currentPlanId

            return (
              <div
                key={plan.id}
                className={cn(
                  'relative flex flex-col',
                  plan.popular && 'shadow-lg ring-2 ring-primary/30',
                )}
              >
                <Card styles={fullWidthStyles}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-xl font-semibold">{plan.name}</h3>
                    </div>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex-1 space-y-6">
                      <div className="text-3xl font-bold text-foreground">
                        {plan.price}
                      </div>
                      <ul className="space-y-3 text-sm text-muted-foreground">
                        {plan.features.map((feature: string, i: number) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-green-500 shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                  <CardFooter>
                    {isBasic ? (
                      isLoggedIn ? (
                        currentPlanId === 'registeredUser' ? (
                          <Button
                            styles={fullWidthCenteredButtonStyles}
                            variant="secondary"
                            fillStyle="outline"
                            isDisabled
                          >
                            Current plan
                          </Button>
                        ) : (
                          <LinkButton
                            href={APP_ROUTES.dashboard.subscription}
                            onPress={() =>
                              trackAnalyticsEvent('pricing_plan_selected', {
                                plan_id: 'registeredUser',
                                action: 'change',
                                location: 'pricing',
                              })
                            }
                            styles={fullWidthCenteredButtonStyles}
                            variant="secondary"
                            fillStyle="outline"
                          >
                            Change to Basic
                          </LinkButton>
                        )
                      ) : (
                        <LinkButton
                          href={APP_ROUTES.login}
                          onPress={() =>
                            trackAnalyticsEvent('pricing_plan_selected', {
                              plan_id: 'registeredUser',
                              action: 'sign_up_free',
                              location: 'pricing',
                            })
                          }
                          styles={fullWidthCenteredButtonStyles}
                          variant="secondary"
                          fillStyle="outline"
                        >
                          Sign up free
                        </LinkButton>
                      )
                    ) : (
                      (() => {
                        const targetPlanId = plan.id as PlanId
                        if (isLoggedIn && targetPlanId === currentPlanId) {
                          return (
                            <Button
                              styles={fullWidthCenteredButtonStyles}
                              isDisabled
                            >
                              Current plan
                            </Button>
                          )
                        }

                        const actionLabel =
                          isLoggedIn &&
                          PLAN_RANK[targetPlanId] < currentRank &&
                          currentPlanId !== 'registeredUser'
                            ? `Change to ${formatActionPlanName(plan.id, plan.name)}`
                            : isLoggedIn
                              ? `Upgrade to ${formatActionPlanName(plan.id, plan.name)}`
                              : plan.buttonText

                        return (
                          <LinkButton
                            href={
                              isLoggedIn
                                ? `${APP_ROUTES.checkout}/${plan.id}`
                                : buildCheckoutRedirect(plan.id)
                            }
                            onPress={() =>
                              trackAnalyticsEvent('pricing_plan_selected', {
                                plan_id: plan.id,
                                action: isLoggedIn
                                  ? 'checkout'
                                  : 'login_redirect',
                                location: 'pricing',
                              })
                            }
                            styles={fullWidthCenteredButtonStyles}
                            variant={plan.popular ? 'primary' : 'secondary'}
                            fillStyle={plan.popular ? 'fill' : 'outline'}
                          >
                            {actionLabel}
                            <ChevronRight />
                          </LinkButton>
                        )
                      })()
                    )}
                  </CardFooter>
                </Card>
              </div>
            )
          })}
        </div>

        {/* Enterprise Section */}
        {enterprisePlan && (
          <div className="mb-20">
            <div className="relative overflow-hidden">
              <Card styles={fullWidthStyles}>
                <CardContent>
                  <div className="grid items-center gap-8 p-2 md:grid-cols-2 md:p-6">
                    <div>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="rounded-lg bg-secondary p-2">
                          <Building className="h-6 w-6 text-primary" />
                        </div>
                        <h2 className="text-2xl font-bold">
                          Enterprise & teams
                        </h2>
                      </div>
                      <p className="mb-6 text-lg text-muted-foreground">
                        {enterprisePlan.description}
                      </p>
                      <LinkButton
                        href="mailto:sales@buildingregs.app"
                        onPress={() =>
                          trackAnalyticsEvent('contact_sales_clicked', {
                            location: 'pricing',
                          })
                        }
                        size="L"
                        variant="secondary"
                        styles={centeredButtonStyles}
                      >
                        Contact sales
                      </LinkButton>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {enterprisePlan.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="h-2 w-2 rounded-full bg-primary shrink-0" />
                          <span className="text-muted-foreground">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <div className="text-center pb-12">
          <h2 className="mb-8 text-2xl font-semibold text-foreground">
            Frequently asked questions
          </h2>
          <div className="grid gap-6 md:grid-cols-2 text-left max-w-4xl mx-auto">
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">
                Can I cancel anytime?
              </h3>
              <p className="text-sm text-muted-foreground">
                Yes, you can cancel your subscription at any time. You'll keep
                access until the end of your billing period.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">
                Do you offer team plans?
              </h3>
              <p className="text-sm text-muted-foreground">
                Yes, our Professional plan includes team management features.
                Contact sales for larger enterprise needs.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">
                What payment methods do you accept?
              </h3>
              <p className="text-sm text-muted-foreground">
                We accept all major credit cards via Stripe.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">
                Is there a free trial?
              </h3>
              <p className="text-sm text-muted-foreground">
                The Basic plan is free forever. You can try our Premium features
                with a monthly subscription.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
